import { ENTITY_ID_LIMIT } from "@colossus/contracts";

export interface Entity {
  readonly id: string;
  readonly name: string;
  readonly parentId?: string | null;
  readonly channelType?: string | null;
}

export interface RoleEntity extends Entity {
  readonly color: string | null;
  readonly position: number | null;
  readonly permissions: string | null;
  readonly managed: boolean | null;
  readonly icon: string | null;
  readonly unicodeEmoji: string | null;
  readonly hoist: boolean | null;
  readonly mentionable: boolean | null;
  readonly secondaryColor: string | null;
  readonly tertiaryColor: string | null;
  readonly heldByColossus: boolean | null;
  readonly botId: string | null;
  readonly overwrites:
    | readonly { readonly channelId: string; readonly allow: string; readonly deny: string }[]
    | null;
}

export const roleLabel = (name: string): string => (name.startsWith("@") ? name : `@${name}`);

export interface EntityList<T extends Entity = Entity> {
  readonly items: readonly T[];
  readonly failed: boolean;
}

export const EMPTY_ENTITIES: EntityList<never> = { items: [], failed: true };

export interface KnownChannel {
  readonly name: string;
  readonly channelType: string | null;
}

export const wantedIds = (wanted: readonly (string | null)[]): readonly string[] => [
  ...new Set(wanted.filter((id): id is string => id !== null)),
];

export const idBatches = (ids: readonly string[]): readonly string[] =>
  Array.from({ length: Math.ceil(ids.length / ENTITY_ID_LIMIT) }, (_, index) =>
    ids.slice(index * ENTITY_ID_LIMIT, (index + 1) * ENTITY_ID_LIMIT).join(","),
  );

const ENTITY_POLL_MS = 2500;

const ENTITY_POLLS = 24;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const pollUntil = async <T>(
  read: () => Promise<T>,
  settled: (value: T) => boolean,
  live: () => boolean = () => true,
  polls = ENTITY_POLLS,
  delayMs = ENTITY_POLL_MS,
): Promise<T> => {
  let last = await read();
  for (let attempt = 1; attempt < polls && live() && !settled(last); attempt += 1) {
    await wait(delayMs);
    if (!live()) break;
    last = await read();
  }
  return last;
};

export const fingerprint = (
  lists: readonly (readonly { readonly id: string; readonly name: string }[])[],
): string => lists.map((list) => list.map((one) => `${one.id}=${one.name}`).join("")).join("");
